import { ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lockup } from '../ui/Lockup';
import { Lang, LangToggle, useI18n } from '../i18n';
import { useDocumentTitle } from '../ui/a11y';
import { useFocusHeadingOnNavigate } from '../ui/focus';
import { A11Y_FEEDBACK_EMAIL, A11Y_STATEMENT_DATE } from '../lib/constants';

// Accessibility statement (saavutettavuusseloste / tillgänglighetsutlåtande),
// required by the Act on the Provision of Digital Services (306/2019).
// Structure follows Traficom's model: scope, compliance status, how it was
// assessed, known shortcomings, feedback channel (14-day reply), and the
// supervisory authority's contact details.
//
// Keep this honest: update the status, shortcomings and date whenever an
// audit finds something or a fix ships (see DESIGN_GUIDELINES.md §10).

type Copy = {
  title: string;
  back: string;
  scopeTitle: string;
  scope: ReactNode;
  statusTitle: string;
  status: ReactNode;
  assessedTitle: string;
  assessed: ReactNode;
  limitsTitle: string;
  limits: ReactNode;
  feedbackTitle: string;
  feedback: (email: ReactNode) => ReactNode;
  emailTbd: string;
  authorityTitle: string;
  authority: ReactNode;
  authorityContactTitle: string;
  dateLine: (d: string) => string;
};

const TRAFICOM = (unit: string, phone: string) => (
  <address>
    {unit}
    <br />
    <a href="https://www.saavutettavuusvaatimukset.fi">www.saavutettavuusvaatimukset.fi</a>
    <br />
    <a href="mailto:saavutettavuus@traficom.fi">saavutettavuus@traficom.fi</a>
    <br />
    {phone}
  </address>
);

const COPY: Record<Lang, Copy> = {
  en: {
    title: 'Accessibility statement',
    back: '← Back',
    scopeTitle: 'Scope',
    scope: (
      <p>
        This statement covers the zencera check-in service at swipe.zencera.fi: the anonymous student check-in and
        the staff sign-in, teacher, school-wide and admin views. The service is used by schools taking part in the
        pilot, and it is available in English, Finnish and Swedish.
      </p>
    ),
    statusTitle: 'Compliance status',
    status: (
      <p>
        We aim to meet the A and AA success criteria of WCAG 2.1, as required by the Act on the Provision of
        Digital Services (306/2019). Based on our self-assessment, the service meets these criteria. It has not yet
        been tested by people who use screen readers or other assistive technology; if that testing finds issues,
        they will be listed below.
      </p>
    ),
    assessedTitle: 'How we assessed accessibility',
    assessed: (
      <ul>
        <li>Automated testing (axe-core, WCAG 2.0, 2.1 and 2.2 A/AA rules) of every view and state.</li>
        <li>Manual checks: keyboard-only use, visible focus, colour contrast, reflow at 320 px, 200% zoom and text spacing.</li>
        <li>Checks in all three languages.</li>
      </ul>
    ),
    limitsTitle: 'Known shortcomings',
    limits: (
      <ul>
        <li>The Finnish and Swedish texts are awaiting review by native speakers.</li>
      </ul>
    ),
    feedbackTitle: 'Did you notice an accessibility issue?',
    feedback: (email) => (
      <p>
        Tell us and we will fix it. Send feedback to {email}. We reply within 14 days. You can also ask for the
        information in another format.
      </p>
    ),
    emailTbd: '(contact address to be added)',
    authorityTitle: 'Supervisory authority',
    authority: (
      <p>
        If you are not satisfied with our reply, or you do not get a reply within two weeks, you can report it to
        the supervisory authority, the Finnish Transport and Communications Agency Traficom.
      </p>
    ),
    authorityContactTitle: 'Contact details of the supervisory authority',
    dateLine: (d) => `This statement was prepared on ${d}.`,
  },
  fi: {
    title: 'Saavutettavuusseloste',
    back: '← Takaisin',
    scopeTitle: 'Selosteen kattama palvelu',
    scope: (
      <p>
        Tämä seloste koskee zencera-kyselypalvelua osoitteessa swipe.zencera.fi: oppilaiden nimetöntä kyselyä sekä
        henkilökunnan kirjautumista, opettajan, koko koulun ja ylläpidon näkymiä. Palvelua käyttävät pilottiin
        osallistuvat koulut, ja se on saatavilla suomeksi, ruotsiksi ja englanniksi.
      </p>
    ),
    statusTitle: 'Digipalvelun saavutettavuuden tila',
    status: (
      <p>
        Tavoitteemme on täyttää WCAG 2.1 -ohjeiden A- ja AA-tason onnistumiskriteerit, kuten laki digitaalisten
        palvelujen tarjoamisesta (306/2019) edellyttää. Oman arviomme perusteella palvelu täyttää nämä kriteerit.
        Palvelua ei ole vielä testattu ruudunlukijoiden tai muiden apuvälineiden käyttäjien kanssa; jos testauksessa
        löytyy puutteita, ne kirjataan alle.
      </p>
    ),
    assessedTitle: 'Miten saavutettavuutta arvioitiin',
    assessed: (
      <ul>
        <li>Automaattinen testaus (axe-core, WCAG 2.0-, 2.1- ja 2.2-ohjeiden A/AA-säännöt) kaikista näkymistä.</li>
        <li>Manuaaliset tarkistukset: näppäimistökäyttö, näkyvä fokus, värikontrastit, mukautuvuus 320 px:n leveydellä, 200 %:n suurennos ja tekstin välistys.</li>
        <li>Tarkistukset kaikilla kolmella kielellä.</li>
      </ul>
    ),
    limitsTitle: 'Tunnetut puutteet',
    limits: (
      <ul>
        <li>Suomen- ja ruotsinkieliset tekstit odottavat äidinkielisen tarkistajan arviota.</li>
      </ul>
    ),
    feedbackTitle: 'Huomasitko puutteita saavutettavuudessa?',
    feedback: (email) => (
      <p>
        Kerro meille, niin korjaamme puutteen. Lähetä palautetta osoitteeseen {email}. Vastaamme 14 päivän kuluessa.
        Voit myös pyytää tietoja toisessa muodossa.
      </p>
    ),
    emailTbd: '(yhteysosoite lisätään)',
    authorityTitle: 'Saavutettavuuden valvonta',
    authority: (
      <p>
        Jos et ole tyytyväinen saamaasi vastaukseen tai et saa vastausta lainkaan kahden viikon aikana, voit tehdä
        ilmoituksen valvontaviranomaiselle, Liikenne- ja viestintävirasto Traficomille.
      </p>
    ),
    authorityContactTitle: 'Valvontaviranomaisen yhteystiedot',
    dateLine: (d) => `Seloste on laadittu ${d}.`,
  },
  sv: {
    title: 'Tillgänglighetsutlåtande',
    back: '← Tillbaka',
    scopeTitle: 'Tjänster som omfattas',
    scope: (
      <p>
        Det här utlåtandet gäller zenceras incheckningstjänst på swipe.zencera.fi: elevernas anonyma incheckning
        samt personalens inloggning, lärar-, skol- och administratörsvyer. Tjänsten används av skolor som deltar i
        piloten och finns på svenska, finska och engelska.
      </p>
    ),
    statusTitle: 'Tjänstens tillgänglighet',
    status: (
      <p>
        Vårt mål är att uppfylla framgångskriterierna på nivå A och AA i WCAG 2.1, i enlighet med lagen om
        tillhandahållande av digitala tjänster (306/2019). Enligt vår egen bedömning uppfyller tjänsten kriterierna.
        Den har ännu inte testats av personer som använder skärmläsare eller andra hjälpmedel; om testningen visar
        brister listas de nedan.
      </p>
    ),
    assessedTitle: 'Hur tillgängligheten har bedömts',
    assessed: (
      <ul>
        <li>Automatiska tester (axe-core, regler för WCAG 2.0, 2.1 och 2.2 A/AA) av alla vyer.</li>
        <li>Manuella kontroller: användning med tangentbord, synligt fokus, färgkontrast, anpassning vid 320 px, 200 % förstoring och textavstånd.</li>
        <li>Kontroller på alla tre språken.</li>
      </ul>
    ),
    limitsTitle: 'Kända brister',
    limits: (
      <ul>
        <li>De finska och svenska texterna väntar på granskning av personer med språket som modersmål.</li>
      </ul>
    ),
    feedbackTitle: 'Har du upptäckt brister i tillgängligheten?',
    feedback: (email) => (
      <p>
        Berätta för oss så åtgärdar vi bristen. Skicka respons till {email}. Vi svarar inom 14 dagar. Du kan också
        be om informationen i ett annat format.
      </p>
    ),
    emailTbd: '(kontaktadress läggs till)',
    authorityTitle: 'Tillsyn',
    authority: (
      <p>
        Om du inte är nöjd med svaret eller inte får något svar inom två veckor kan du anmäla det till
        tillsynsmyndigheten, Transport- och kommunikationsverket Traficom.
      </p>
    ),
    authorityContactTitle: 'Tillsynsmyndighetens kontaktuppgifter',
    dateLine: (d) => `Utlåtandet har utarbetats ${d}.`,
  },
};

const AUTHORITY_UNIT: Record<Lang, [string, string]> = {
  en: ['Finnish Transport and Communications Agency Traficom, Digital accessibility supervision unit', 'Switchboard +358 29 534 5000'],
  fi: ['Liikenne- ja viestintävirasto Traficom, Digitaalisen esteettömyyden ja saavutettavuuden valvontayksikkö', 'Vaihde 029 534 5000'],
  sv: ['Transport- och kommunikationsverket Traficom, Enheten för tillsyn över digital tillgänglighet', 'Växel 029 534 5000'],
};

export function AccessibilityStatement() {
  const { lang, locale } = useI18n();
  const c = COPY[lang];
  const navigate = useNavigate();
  const h1 = useRef<HTMLHeadingElement>(null);
  useDocumentTitle(c.title);
  useFocusHeadingOnNavigate(h1);

  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(
    new Date(A11Y_STATEMENT_DATE)
  );
  const email = A11Y_FEEDBACK_EMAIL ? (
    <a href={`mailto:${A11Y_FEEDBACK_EMAIL}`}>{A11Y_FEEDBACK_EMAIL}</a>
  ) : (
    <strong>{c.emailTbd}</strong>
  );
  const [unit, phone] = AUTHORITY_UNIT[lang];

  return (
    <div className="statement-page">
      <header className="statement-top">
        <Lockup />
        <LangToggle />
      </header>
      <main className="panel statement fade-in">
        {/* Always offer a way back; with no history (opened from a link), go to the start page. */}
        <button type="button" className="link-btn" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}>
          {c.back}
        </button>
        <h1 ref={h1} tabIndex={-1}>
          {c.title}
        </h1>
        <section>
          <h2>{c.scopeTitle}</h2>
          {c.scope}
        </section>
        <section>
          <h2>{c.statusTitle}</h2>
          {c.status}
        </section>
        <section>
          <h2>{c.assessedTitle}</h2>
          {c.assessed}
        </section>
        <section>
          <h2>{c.limitsTitle}</h2>
          {c.limits}
        </section>
        <section>
          <h2>{c.feedbackTitle}</h2>
          {c.feedback(email)}
        </section>
        <section>
          <h2>{c.authorityTitle}</h2>
          {c.authority}
          <h3>{c.authorityContactTitle}</h3>
          {TRAFICOM(unit, phone)}
        </section>
        <p className="text-muted">{c.dateLine(date)}</p>
      </main>
    </div>
  );
}
